import { useCallback, useEffect, useRef } from 'react';
import { isNil, snakeCase } from 'lodash-es';

import { trackV2 } from 'utils/analytics';
import { FEED_ENTRY_TYPES } from '../constants';
import { useFeedContext } from '../components/shared/FeedContext';
import { devLog } from '../../../utils/devUtils';

const useFeedAnalytics = ({
  page,
  entity,
  entityId,
  athleteProfileId,
  feedType,
  index,
  clubId,
  viewingAthleteId,
  activityIds,
  element = null,
  properties,
  inView
}) => {
  if (isNil(inView)) {
    devLog(
      `useIntersectionObserver#inView is required for analytics tracking. entity - ${entity}`
    );
  }

  const { entityState } = useFeedContext() || {
    entityState: { isRestoringScrollPosition: false }
  };

  const { isRestoringScrollPosition } = entityState;
  /**
   * Top-level {properties} -  These belong to the main feed card entries eg. Activity, Post,
   * ChallengeJoin, GroupActivity etc...
   *
   * These values are shared with all the children elements eg Map inside of Activity will automatically have
   * these values.
   */
  const parentLevelProperties = (() => {
    const entityName = snakeCase(entity);
    const inGroup = entityName === FEED_ENTRY_TYPES.groupActivity;
    const source =
      feedType === 'club' && page === 'club' ? 'club_detail' : feedType;
    const hash = {
      in_group: inGroup,
      source,
      viewing_athlete_id: viewingAthleteId,
      rank: index + 1,
      entity_id: entityId
    };
    if (feedType === 'club' || feedType === 'clubs_feed') {
      hash.club_id = clubId;
    }
    if (feedType === 'profile') {
      hash.profile_athlete = athleteProfileId;
    }
    if (entityName === FEED_ENTRY_TYPES.activity) {
      hash.activity_id = entityId;
    }
    if (entityName === FEED_ENTRY_TYPES.post) {
      hash.post_id = entityId;
    }
    if (entityName === FEED_ENTRY_TYPES.club) {
      hash.club_id = entityId;
    }
    if (entityName === FEED_ENTRY_TYPES.challenge) {
      hash.challenge_id = entityId;
    }
    if (entityName === FEED_ENTRY_TYPES.groupActivity) {
      hash.headline_activity_id = entityId;
      hash.group_activity_ids = activityIds;
    }
    if (
      entityName === FEED_ENTRY_TYPES.fancyPromo ||
      entityName === FEED_ENTRY_TYPES.simplePromo
    ) {
      // note: promos do not have entity id and instead are using trackable id as a unique identifier (also see Feed.js)
      hash.trackable_id = entityId;
    }
    if (entityName === FEED_ENTRY_TYPES.performanceGoalCreated) {
      hash.performance_goal_id = entityId;
    }
    return { ...hash, ...properties };
  })();

  /**
   * Function used by all feed components to track analytics.
   * When an element uses this function, all the props defined by its parent will
   * automatically be tracked on top of the element specific props.
   */
  const trackAnalytics = useCallback(
    (childElementFields) => {
      const feedAnalyticsCommonFields = {
        category: 'feed',
        page: snakeCase(entity)
      };
      trackV2({
        ...feedAnalyticsCommonFields,
        ...childElementFields,
        properties: {
          ...parentLevelProperties,
          ...childElementFields.properties
        }
      });
    },
    [entity, parentLevelProperties]
  );

  // only report inView status for any re-renders after the initial
  // component mount
  const isFirstRenderRef = useRef(true);

  // report screen enter/exit when the inView status of an entry changes
  useEffect(() => {
    if (!isFirstRenderRef.current) {
      if (!isRestoringScrollPosition) {
        // do not track analytics during scroll position restoration
        trackAnalytics({
          element,
          action: inView ? 'screen_enter' : 'screen_exit'
        });
      }
    } else {
      // component has now been mounted. Any changes to inViewStatus will now
      // log enter/exit analytics events
      isFirstRenderRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, entityId, element]);

  return {
    isFirstRenderRef,
    trackAnalytics
  };
};

export default useFeedAnalytics;
